import React, { useEffect, useState, useRef } from 'react'
import { string, func } from 'prop-types'
import styled from 'styled-components'
import { rgba, stripUnit } from 'polished'

import { white, offWhite, greyDark, grey, borderColor } from 'styles/colors'
import * as spacing from 'styles/spacing'
import { media } from 'styles/media'
import { fontWeights } from 'styles/typography'
import { animationTime, animationCurve } from 'styles/global'
import SmallCaps from 'components/SmallCaps'
import faqs from 'utils/plus-faqs'

const Container = styled.div`
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
`

const Wrapper = styled.div`
  position: relative;
`

const Input = styled.input`
  width: 100%;
  background-color: ${white};
  color: ${greyDark};
  border-radius: ${spacing.xxLarge};
  padding: ${spacing.medium} ${spacing.large};

  ${media.small`
    font-size: 18px;
  `};

  ${media.medium`
    font-size: 20px;
    padding: ${stripUnit(spacing.medium) * 1.25 + 'px'} ${spacing.xLarge};
  `};

  ${media.large`
    font-size: 22px;
  `};

  ${media.xLarge`
    padding: ${spacing.large} ${stripUnit(spacing.xLarge) * 1.15 + 'px'};
  `};

  &::placeholder {
    color: ${rgba(greyDark, 0.3)};
  }
`

const Menu = styled.div`
  position: absolute;
  z-index: 10;
  width: 100%;
  background-color: ${white};
  border-radius: ${spacing.xLarge};
  margin-top: ${spacing.small};
  overflow: hidden;
`

const Scroll = styled.div`
  overflow-y: auto;
  max-height: 68vh;
`

const ResultContainer = styled.div`
  position: relative;
  text-align: left;
  cursor: pointer;
  padding: ${spacing.large};
  transition: background-color ${animationTime} ${animationCurve};

  ${media.medium`
    padding: ${spacing.xLarge};
  `};

  ${media.large`
    padding: ${spacing.xLarge} ${spacing.xxLarge};
  `};

  &:hover {
    background-color: ${offWhite};
  }

  & + & {
    border-top: 1px solid ${borderColor};
  }
`

const Category = styled(SmallCaps)`
  display: block;
  font-weight: ${fontWeights.regular};
  color: ${grey};
  margin-bottom: ${spacing.tiny};
`

const Question = styled.p`
  color: ${greyDark};
  font-weight: ${fontWeights.bold};
  margin-bottom: ${spacing.small};
`

const Answer = styled.div`
  color: ${grey};
  -webkit-box-orient: vertical;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis;
  height: 42px;

  ${media.medium`
    height: 44px;
  `};

  ${media.large`
    height: 48px;
  `};
`

const EmptyState = styled.p`
  padding: ${spacing.xLarge};
  color: ${grey};
`

const SearchBox = ({ navigateTo }) => {
  const [indexReady, setIndexReady] = useState(false)
  const [results, setResults] = useState(null) // null to hide pane, [] for empty results
  const searchEl = useRef(null)
  const index = useRef()

  useEffect(() => {
    buildIndex()

    document.body.addEventListener('click', closeAutocomplete)

    return () => {
      document.body.removeEventListener('click', closeAutocomplete)
    }
  }, [])

  const closeAutocomplete = e => {
    if (searchEl.current.contains(e.target)) return
    setResults(null)
  }

  const buildIndex = async () => {
    const { default: FlexSearch } = await import('flexsearch')

    index.current = new FlexSearch({
      async: true,
      doc: {
        id: 'id',
        field: ['question', 'response']
      }
    })

    const flatFAQs = faqs.reduce(
      (acc, category, i) =>
        acc.concat(
          category.content.map((c, j) => ({ ...c, category: category.title, categoryIdx: i, questionIdx: j }))
        ),
      []
    )
    const indexedFAQs = flatFAQs.map((faq, i) => ({ ...faq, id: i }))
    index.current.add(indexedFAQs)
    setIndexReady(true)
  }

  const search = async e => {
    if (!indexReady) return
    const query = e.target.value
    if (query.trim() === '') {
      setResults(null)
      return
    }
    const results = await index.current.search(query)
    setResults(results)
  }

  const handleFocus = e => {
    if (e.target.value.trim() === '') return
    search(e)
  }

  const handleResultClick = result => {
    navigateTo(result)
    setResults(null)
  }

  return (
    <Container ref={searchEl}>
      <Wrapper>
        <Input placeholder="Type keywords to find a topic" onKeyUp={search} onFocus={handleFocus} />
        {results && (
          <Menu>
            <Scroll>
              {results.length ? (
                results.map((result, i) => <Result {...result} onClick={() => handleResultClick(result)} key={i} />)
              ) : (
                <EmptyState>No results found</EmptyState>
              )}
            </Scroll>
          </Menu>
        )}
      </Wrapper>
    </Container>
  )
}

SearchBox.propTypes = {
  navigateTo: func.isRequired
}

const Result = ({ category, question, response, onClick }) => (
  <ResultContainer onClick={onClick}>
    <Category>{category}</Category>
    <Question>{question}</Question>
    <Answer dangerouslySetInnerHTML={{ __html: response }} />
  </ResultContainer>
)

Result.propTypes = {
  onClick: func.isRequired,
  category: string.isRequired,
  question: string.isRequired,
  response: string.isRequired
}

export default SearchBox
